/**
 * NotificationPrefsPanel — opt-out toggle for the `lead_found_email`
 * notification (Iter75). Mounted inside /account.
 *
 * Backed by:
 *   GET /api/notifications/preferences  → { lead_found_email, throttle_window_min }
 *   PUT /api/notifications/preferences  { lead_found_email: bool }
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bell, BellOff, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationPrefsPanel({ isPt = true }) {
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/notifications/preferences`)
      .then(({ data }) => setPrefs(data))
      .catch(() => setPrefs({ lead_found_email: true, throttle_window_min: 30 }));
  }, []);

  const toggle = async () => {
    if (!prefs) return;
    setBusy(true);
    const next = !prefs.lead_found_email;
    try {
      const { data } = await axios.put(`${API}/notifications/preferences`,
        { lead_found_email: next });
      setPrefs((p) => ({ ...p, lead_found_email: data.lead_found_email }));
      toast.success(
        data.lead_found_email
          ? (isPt ? "Notificações ativadas" : "Notifications enabled")
          : (isPt ? "Notificações silenciadas" : "Notifications muted"),
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message);
    } finally { setBusy(false); }
  };

  const enabled = prefs?.lead_found_email !== false;
  const Icon = enabled ? Bell : BellOff;

  return (
    <section className="al-card p-6" data-testid="account-notifications">
      <h2 className="text-[13px] uppercase tracking-wide text-[var(--al-text-muted)] font-semibold mb-4 flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" /> {isPt ? "Notificações" : "Notifications"}
      </h2>

      <div className="flex items-start gap-3">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${enabled ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <div className="font-bold text-[14px]">
            {isPt ? "E-mail quando uma busca encontrar um lead" : "Email when a search finds a lead"}
          </div>
          <div className="text-[12px] text-[var(--al-text-muted)] mt-1 leading-relaxed">
            {isPt
              ? `Você recebe um e-mail toda vez que Maps ou Public Works retornarem leads. Janela mínima entre e-mails: ${prefs?.throttle_window_min ?? 30} min (digest).`
              : `You receive an email whenever Maps or Public Works returns leads. Minimum window between emails: ${prefs?.throttle_window_min ?? 30} min (digest).`}
          </div>
        </div>
        <button type="button" onClick={toggle} disabled={busy || !prefs}
          data-testid="notif-toggle"
          aria-pressed={enabled}
          className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-300"} ${busy ? "opacity-60" : ""}`}>
          <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
          {busy && (
            <span className="absolute inset-0 flex items-center justify-center text-white">
              <Loader2 className="w-3 h-3 animate-spin" />
            </span>
          )}
        </button>
      </div>
    </section>
  );
}
